import { computed, type ComputedRef } from 'vue'
import type { HiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'

// VDataTable-shaped column definitions shared by the individual-investor
// Funds and SPVs list pages.
//
// Data shape: assumes rows are entities returned by
// `individualStore.fetchEntities`, which merges /investor-sets/:id with
// /insights — i.e. `insights.*` and `structure.*` are nested. If a future
// caller needs to consume the flat group-page shape from
// `entityStore.fetchEntitiesV3`, add a `surface` option here and switch
// flatten paths.

type IndividualEntityType = 'fund' | 'spv'

interface Options {
  entityType: IndividualEntityType
  hidden: ComputedRef<HiddenMetrics> | { value: HiddenMetrics }
}

export interface IndividualEntityColumn {
  key: string
  name: string
  label: string
  type: 'string' | 'currency' | 'percent' | 'date' | 'number' | 'integer' | 'boolean'
  align: 'left' | 'right' | 'center'
  hidden?: boolean
  flatten?: (item: any) => any
}

export function useIndividualEntityColumns({ entityType, hidden }: Options): ComputedRef<IndividualEntityColumn[]> {
  return computed(() => {
    const cashAccessible = !hidden.value?.cashPosition
    const showSpvManager = entityType === 'spv'

    const cols: (IndividualEntityColumn | false)[] = [
      {
        key: 'lifecycle_state',
        name: 'Status',
        label: 'Status',
        type: 'string',
        align: 'left',
        flatten: (item: any) => item.lifecycle_state,
      },
      {
        key: 'name',
        name: 'Name',
        label: 'Name',
        type: 'string',
        align: 'left',
      },
      {
        key: 'committed',
        name: 'Committed',
        label: 'Committed',
        type: 'currency',
        align: 'right',
        flatten: (item: any) => item.insights?.committed,
      },
      {
        key: 'called',
        name: 'Called',
        label: 'Called',
        type: 'currency',
        align: 'right',
        hidden: true,
        flatten: (item: any) => item.insights?.called_total,
      },
      {
        key: 'commitment_remaining',
        name: 'Commitment Remaining',
        label: 'Commitment Remaining',
        type: 'currency',
        align: 'right',
        hidden: true,
        flatten: (item: any) => item.insights?.commitment_remaining,
      },
      {
        key: 'distributed',
        name: 'Distributed',
        label: 'Distributed',
        type: 'currency',
        align: 'right',
        hidden: true,
        flatten: (item: any) => item.insights?.portfolio_distributed,
      },
      cashAccessible && {
        key: 'cash_position',
        name: 'Cash Position',
        label: 'Cash Position',
        type: 'currency',
        align: 'right',
        hidden: true,
        flatten: (item: any) => item.insights?.cash_position,
      },
      {
        key: 'management_fee_rate',
        name: 'Management Fee %',
        label: 'Management Fee %',
        type: 'percent',
        align: 'right',
        flatten: (item: any) => item.structure?.management_fee_rate,
      },
      {
        key: 'carried_interest_rate',
        name: 'Carried Interest %',
        label: 'Carried Interest %',
        type: 'percent',
        align: 'right',
        flatten: (item: any) => item.structure?.carried_interest_rate,
      },
      {
        key: 'preferred_return_rate',
        name: 'Preferred Return %',
        label: 'Preferred Return %',
        type: 'percent',
        align: 'right',
        flatten: (item: any) => item.structure?.preferred_return_rate,
      },
      {
        key: 'distributed_carried_interest',
        name: 'Carried Interest',
        label: 'Carried Interest',
        type: 'currency',
        align: 'right',
        hidden: true,
        flatten: (item: any) => item.insights?.distributed_carried_interest,
      },
      {
        key: 'legal_name',
        name: 'Legal Name',
        label: 'Legal Name',
        type: 'string',
        align: 'left',
        hidden: true,
      },
      {
        key: 'date_established',
        name: 'Date Established',
        label: 'Date Established',
        type: 'date',
        align: 'left',
      },
      {
        key: 'group_name',
        name: 'Group',
        label: 'Group',
        type: 'string',
        align: 'left',
        hidden: true,
        flatten: (item: any) => item.group?.name,
      },
      {
        key: 'type',
        name: 'Legal Structure',
        label: 'Legal Structure',
        type: 'string',
        align: 'left',
      },
      {
        key: 'tax_id',
        name: 'EIN',
        label: 'EIN',
        type: 'string',
        align: 'left',
      },
      {
        key: 'document_status',
        name: 'Document Status',
        label: 'Document Status',
        type: 'string',
        align: 'left',
      },
      {
        key: 'selected_subscriptions',
        name: 'Subscription',
        label: 'Subscription',
        type: 'string',
        align: 'left',
      },
      // SPV-only: funds are multi-managed and surface those via the `admins`
      // column instead. Matches the `showSpvManager` gate in entities-page.
      showSpvManager && {
        key: 'manager',
        name: 'SPV Manager',
        label: 'SPV Manager',
        type: 'string',
        align: 'left',
      },
      {
        key: 'admins',
        name: 'Managers',
        label: 'Managers',
        type: 'string',
        align: 'left',
      },
    ]

    return cols.filter(Boolean) as IndividualEntityColumn[]
  })
}
